import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ThemeToggleTwoComponent } from '../../../shared/components/common/theme-toggle-two/theme-toggle-two.component';

/**
 * Landing publica (rutas `/` y `/home`).
 *
 * Reglas de diseno: esta pagina usa EXACTAMENTE el mismo sistema visual que el
 * resto del CRM — tokens `brand-*` / `gray-*` de `styles.css`, tipografia Outfit,
 * tarjetas `rounded-2xl border border-gray-200 ... dark:border-gray-800` y modo
 * oscuro por la variante `dark:` de Tailwind (ThemeService pone la clase `dark`
 * en <html>, asi que NO hace falta duplicar la marcacion por tema como hacia la
 * version anterior, que repetia el hero entero en un @if/@else). Si tocas colores
 * aqui, usa tokens, no hex sueltos.
 *
 * El logo es el lockup oficial (`partner-crm-full[-white].png`), el mismo del
 * sidebar y de la cabecera de la app: se conmuta con `dark:hidden` / `hidden dark:block`.
 */
@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [CommonModule, RouterModule, ThemeToggleTwoComponent],
  styles: [`
    :host { display: block; }

    /* Offset del scroll suave (scrollToSection) por el header flotante. */
    :host section[id] { scroll-margin-top: 96px; }

    /* Reticula de fondo del hero: gradientes, sin assets. */
    .hero-grid {
      background-image:
        linear-gradient(to right, rgba(16, 24, 40, 0.045) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(16, 24, 40, 0.045) 1px, transparent 1px);
      background-size: 56px 56px;
      -webkit-mask-image: radial-gradient(ellipse 80% 60% at 50% 0%, #000 40%, transparent 100%);
      mask-image: radial-gradient(ellipse 80% 60% at 50% 0%, #000 40%, transparent 100%);
    }
    :host-context(.dark) .hero-grid {
      background-image:
        linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    }

    /* Aparicion progresiva al entrar en viewport (IntersectionObserver). */
    .reveal {
      opacity: 0;
      transform: translateY(24px);
      transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
      will-change: opacity, transform;
    }
    .reveal.is-visible { opacity: 1; transform: none; }

    /* Vaiven con from/to + direction: alternate (mas corto que 3 keyframes). */
    .float-slow { animation: floatSlow 3.5s ease-in-out infinite alternate; }
    @keyframes floatSlow {
      from { transform: translateY(0); }
      to { transform: translateY(-10px); }
    }

    /* Cinta de integraciones: el track lleva la lista DUPLICADA, por eso -50%. */
    .marquee-mask {
      -webkit-mask-image: linear-gradient(to right, transparent, #000 12%, #000 88%, transparent);
      mask-image: linear-gradient(to right, transparent, #000 12%, #000 88%, transparent);
    }
    .marquee-track { animation: marquee 38s linear infinite; }
    @keyframes marquee {
      from { transform: translateX(0); }
      to { transform: translateX(-50%); }
    }

    @media (prefers-reduced-motion: reduce) {
      .reveal { opacity: 1; transform: none; transition: none; }
      .float-slow, .marquee-track { animation: none; }
    }
  `],
  template: `
    <div class="min-h-screen overflow-x-hidden bg-white text-gray-900 antialiased dark:bg-gray-900 dark:text-white/90">

      <!-- ============================= HEADER ============================= -->
      <header class="fixed inset-x-0 top-0 z-50 px-4 sm:px-6 lg:px-8">
        <nav
          class="mx-auto mt-4 max-w-6xl rounded-2xl border transition-all duration-300"
          [class]="scrolled
            ? 'border-gray-200 bg-white/80 shadow-sm backdrop-blur-xl dark:border-gray-800 dark:bg-gray-900/80'
            : 'border-transparent bg-transparent'">
          <div class="flex h-16 items-center justify-between gap-4 px-4 sm:px-5">
            <a routerLink="/" class="flex shrink-0 items-center" aria-label="Partner CRM">
              <img src="images/logo-crm/partner-crm-full.png" alt="Partner CRM"
                class="h-8 w-auto object-contain dark:hidden" />
              <img src="images/logo-crm/partner-crm-full-white.png" alt="Partner CRM"
                class="hidden h-8 w-auto object-contain dark:block" />
            </a>

            <div class="hidden items-center gap-1 lg:flex">
              @for (link of navLinks; track link.id) {
                <a (click)="scrollToSection(link.id); $event.preventDefault()"
                  class="cursor-pointer rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white">
                  {{ link.label }}
                </a>
              }
              <a routerLink="/terminos-y-condiciones"
                class="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white">
                Términos
              </a>
            </div>

            <div class="flex items-center gap-2">
              <a routerLink="/signin"
                class="hidden rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5 sm:block">
                Iniciar sesión
              </a>
              <a routerLink="/signup"
                class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-theme-xs transition-colors hover:bg-brand-600">
                Solicitar acceso
              </a>
              <button type="button" (click)="mobileOpen = !mobileOpen"
                class="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-white/5 lg:hidden"
                [attr.aria-expanded]="mobileOpen" aria-label="Abrir menú">
                @if (!mobileOpen) {
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-width="2" d="M4 7h16M4 12h16M4 17h16" />
                  </svg>
                } @else {
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-width="2" d="M6 6l12 12M18 6L6 18" />
                  </svg>
                }
              </button>
            </div>
          </div>

          @if (mobileOpen) {
            <div class="border-t border-gray-200 px-4 py-3 dark:border-gray-800 lg:hidden">
              <div class="flex flex-col">
                @for (link of navLinks; track link.id) {
                  <a (click)="scrollToSection(link.id); mobileOpen = false; $event.preventDefault()"
                    class="cursor-pointer rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5">
                    {{ link.label }}
                  </a>
                }
                <a routerLink="/terminos-y-condiciones"
                  class="rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5">
                  Términos y condiciones
                </a>
                <a routerLink="/signin"
                  class="rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5 sm:hidden">
                  Iniciar sesión
                </a>
              </div>
            </div>
          }
        </nav>
      </header>

      <!-- ============================== HERO ============================== -->
      <section class="relative overflow-hidden pb-20 pt-32 sm:pt-40">
        <div class="hero-grid pointer-events-none absolute inset-0"></div>
        <div class="pointer-events-none absolute inset-x-0 -top-40 flex justify-center">
          <div class="h-[520px] w-[820px] rounded-full bg-brand-500/10 blur-[130px] dark:bg-brand-500/20"></div>
        </div>

        <div class="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="grid items-center gap-14 lg:grid-cols-12">

            <div class="reveal lg:col-span-6">
              <span class="inline-flex items-center gap-2 rounded-full border border-brand-500/20 bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-600 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-300">
                <span class="relative flex h-2 w-2">
                  <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-500 opacity-60"></span>
                  <span class="relative inline-flex h-2 w-2 rounded-full bg-brand-500"></span>
                </span>
                CRM multiempresa · versión 2.0
              </span>

              <h1 class="mt-6 text-4xl font-bold leading-[1.1] tracking-tight text-gray-900 dark:text-white sm:text-5xl xl:text-6xl">
                Todo tu ciclo comercial
                <span class="bg-gradient-to-r from-brand-500 to-brand-400 bg-clip-text text-transparent">en una sola plataforma</span>
              </h1>

              <p class="mt-6 max-w-xl text-base leading-relaxed text-gray-600 dark:text-gray-400 sm:text-lg">
                Contactos, leads, negocios, cotizaciones, tickets y marketing conectados —
                con automatizaciones, chatbots y auditoría de cada movimiento. Partner CRM
                le da a tu equipo un único lugar donde vender y dar servicio.
              </p>

              <div class="mt-9 flex flex-col gap-3 sm:flex-row">
                <a routerLink="/signup"
                  class="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-7 py-3.5 text-base font-semibold text-white shadow-theme-md transition-all hover:-translate-y-0.5 hover:bg-brand-600 active:translate-y-0">
                  Solicitar acceso
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
                <a routerLink="/signin"
                  class="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-7 py-3.5 text-base font-medium text-gray-700 shadow-theme-xs transition-all hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 active:translate-y-0 dark:border-gray-800 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14M3 12a9 9 0 1018 0 9 9 0 00-18 0z" />
                  </svg>
                  Entrar a mi cuenta
                </a>
              </div>

              <ul class="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
                @for (point of heroPoints; track point) {
                  <li class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <svg class="h-4 w-4 shrink-0 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                    </svg>
                    {{ point }}
                  </li>
                }
              </ul>
            </div>

            <!-- Mockup de la app: replica el shell real (sidebar + kanban de negocios). -->
            <div class="reveal lg:col-span-6">
              <div class="relative">
                <div class="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-brand-500/20 via-brand-400/10 to-transparent blur-2xl"></div>

                <div class="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-xl dark:border-gray-800 dark:bg-gray-dark">
                  <div class="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
                    <span class="h-3 w-3 rounded-full bg-error-400"></span>
                    <span class="h-3 w-3 rounded-full bg-orange-400"></span>
                    <span class="h-3 w-3 rounded-full bg-success-400"></span>
                    <span class="mx-auto rounded-md bg-white px-3 py-1 text-[11px] text-gray-500 shadow-theme-xs dark:bg-white/5 dark:text-gray-400">
                      app.partnercrm.com
                    </span>
                  </div>

                  <div class="flex">
                    <aside class="hidden w-36 shrink-0 border-r border-gray-200 p-3 dark:border-gray-800 sm:block">
                      @for (item of mockMenu; track item.label) {
                        <div class="mb-1 flex items-center gap-2 rounded-lg px-2.5 py-2 text-[11px] font-medium"
                          [class]="item.active
                            ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300'
                            : 'text-gray-500 dark:text-gray-400'">
                          <span class="h-1.5 w-1.5 rounded-full"
                            [class]="item.active ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-700'"></span>
                          {{ item.label }}
                        </div>
                      }
                    </aside>

                    <div class="min-w-0 flex-1 p-4">
                      <div class="mb-4 grid grid-cols-3 gap-3">
                        @for (kpi of mockKpis; track kpi.label) {
                          <div class="rounded-xl border border-gray-200 p-3 dark:border-gray-800">
                            <p class="text-lg font-bold text-gray-900 dark:text-white">{{ kpi.value }}</p>
                            <p class="text-[10px] text-gray-500 dark:text-gray-400">{{ kpi.label }}</p>
                            <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
                              <div class="h-full rounded-full bg-brand-500" [style.width]="kpi.width"></div>
                            </div>
                          </div>
                        }
                      </div>

                      <div class="grid grid-cols-3 gap-3">
                        @for (col of mockKanban; track col.title) {
                          <div class="rounded-xl bg-gray-50 p-2.5 dark:bg-white/[0.03]">
                            <div class="mb-2 flex items-center justify-between">
                              <span class="text-[10px] font-semibold text-gray-600 dark:text-gray-400">{{ col.title }}</span>
                              <span class="rounded-full bg-white px-1.5 text-[10px] text-gray-500 dark:bg-white/10 dark:text-gray-400">{{ col.count }}</span>
                            </div>
                            <div class="space-y-2">
                              @for (card of col.cards; track card.name) {
                                <div class="rounded-lg border border-gray-200 bg-white p-2 shadow-theme-xs dark:border-gray-800 dark:bg-gray-900">
                                  <p class="truncate text-[10px] font-semibold text-gray-800 dark:text-gray-200">{{ card.name }}</p>
                                  <p class="mt-0.5 text-[10px] text-brand-500">{{ card.amount }}</p>
                                </div>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tarjetas flotantes: dan profundidad y cuentan dos features de un vistazo. -->
                <div class="float-slow absolute -bottom-6 -left-8 hidden rounded-xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark md:block">
                  <div class="flex items-center gap-2.5">
                    <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-400">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </span>
                    <div>
                      <p class="text-[11px] font-semibold text-gray-800 dark:text-gray-200">Workflow ejecutado</p>
                      <p class="text-[10px] text-gray-500 dark:text-gray-400">Lead asignado y notificado</p>
                    </div>
                  </div>
                </div>

                <div class="float-slow absolute -right-8 -top-6 hidden rounded-xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark md:block" style="animation-delay: 1.4s">
                  <div class="flex items-center gap-2.5">
                    <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M21 12a8 8 0 01-11.6 7.1L3 21l1.9-6.4A8 8 0 1121 12z" />
                      </svg>
                    </span>
                    <div>
                      <p class="text-[11px] font-semibold text-gray-800 dark:text-gray-200">Chat en vivo</p>
                      <p class="text-[10px] text-gray-500 dark:text-gray-400">3 conversaciones activas</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ========================== INTEGRACIONES ========================= -->
      <section class="border-y border-gray-200 bg-gray-50 py-12 dark:border-gray-800 dark:bg-white/[0.02]">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p class="mb-8 text-center text-xs font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
            Se conecta con las herramientas que ya usas
          </p>
          <div class="marquee-mask relative overflow-hidden">
            <div class="marquee-track flex w-max items-center gap-14">
              @for (item of marqueeIntegrations; track $index) {
                <div class="flex items-center gap-2.5 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300">
                  <span class="inline-flex h-6 w-6 items-center justify-center" [innerHTML]="item.icon"></span>
                  <span class="whitespace-nowrap text-sm font-semibold tracking-tight">{{ item.name }}</span>
                </div>
              }
            </div>
          </div>
        </div>
      </section>

      <!-- ============================= MÓDULOS ============================ -->
      <section id="features" class="py-24">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="reveal mx-auto mb-14 max-w-2xl text-center">
            <span class="inline-block rounded-full border border-brand-500/20 bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-600 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-300">
              Módulos
            </span>
            <h2 class="mt-5 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Un CRM completo, no una agenda de contactos
            </h2>
            <p class="mt-4 text-gray-600 dark:text-gray-400">
              Cada módulo comparte los mismos datos, los mismos permisos y la misma auditoría.
              Nada de encadenar cinco herramientas para cerrar una venta.
            </p>
          </div>

          <div class="grid grid-cols-12 gap-5">

            <!-- Pipeline -->
            <article class="reveal col-span-12 flex flex-col rounded-2xl border border-gray-200 bg-white p-7 shadow-sm transition-colors hover:border-brand-500/40 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-7">
              <div class="mb-6 flex items-start gap-3">
                <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-500 text-white shadow-theme-sm">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                </span>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Leads y negocios</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Pipeline kanban con los ciclos y etapas de tu operación</p>
                </div>
              </div>

              <div class="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
                @for (col of pipeline; track col.title) {
                  <div class="flex flex-col rounded-xl bg-gray-50 p-3 dark:bg-white/[0.04]">
                    <div class="mb-2.5 flex items-center justify-between">
                      <span class="text-xs font-semibold text-gray-600 dark:text-gray-400">{{ col.title }}</span>
                      <span class="text-[11px] text-gray-400 dark:text-gray-500">{{ col.count }}</span>
                    </div>
                    <div class="space-y-2">
                      @for (bar of col.bars; track $index) {
                        <div class="h-7 rounded-lg border border-brand-500/20 bg-brand-500/10"></div>
                      }
                    </div>
                  </div>
                }
              </div>

              <p class="mt-4 text-xs text-gray-500 dark:text-gray-400">
                Arrastra una tarjeta para cambiarla de etapa: el movimiento queda en la línea de
                tiempo del negocio y puede disparar un workflow.
              </p>
            </article>

            <!-- Automatizaciones -->
            <article class="reveal col-span-12 rounded-2xl border border-gray-200 bg-white p-7 shadow-sm transition-colors hover:border-brand-500/40 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-5">
              <div class="mb-6 flex items-start gap-3">
                <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-500 text-white shadow-theme-sm">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </span>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Automatizaciones</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Workflows visuales: disparador, condición y acción</p>
                </div>
              </div>

              <div class="space-y-2.5">
                @for (step of workflowSteps; track step.label) {
                  <div class="flex items-center gap-3 rounded-xl bg-gray-50 p-3 dark:bg-white/[0.04]">
                    <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold" [class]="step.tone">
                      {{ $index + 1 }}
                    </span>
                    <span class="text-sm text-gray-700 dark:text-gray-300">{{ step.label }}</span>
                  </div>
                }
              </div>
            </article>

            <!-- Tickets -->
            <article class="reveal col-span-12 rounded-2xl border border-gray-200 bg-white p-7 shadow-sm transition-colors hover:border-brand-500/40 dark:border-gray-800 dark:bg-white/[0.03] md:col-span-6 lg:col-span-4">
              <div class="mb-6 flex items-start gap-3">
                <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-500 text-white shadow-theme-sm">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h4m1-12H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-4z" />
                  </svg>
                </span>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Tickets con SLA</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Soporte medido, no improvisado</p>
                </div>
              </div>
              <div class="space-y-2.5">
                @for (row of ticketRows; track row.label) {
                  <div class="flex items-center justify-between rounded-xl bg-gray-50 px-3.5 py-2.5 dark:bg-white/[0.04]">
                    <span class="text-sm text-gray-600 dark:text-gray-400">{{ row.label }}</span>
                    <span class="text-base font-bold" [class]="row.tone">{{ row.value }}</span>
                  </div>
                }
              </div>
            </article>

            <!-- Cotizaciones -->
            <article class="reveal col-span-12 rounded-2xl border border-gray-200 bg-white p-7 shadow-sm transition-colors hover:border-brand-500/40 dark:border-gray-800 dark:bg-white/[0.03] md:col-span-6 lg:col-span-4">
              <div class="mb-6 flex items-start gap-3">
                <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-500 text-white shadow-theme-sm">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 14l2 2 4-4m5 6a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h8l6 6v10z" />
                  </svg>
                </span>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Cotizaciones</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Del catálogo a la firma electrónica</p>
                </div>
              </div>
              <div class="rounded-xl bg-gray-50 p-4 dark:bg-white/[0.04]">
                <div class="mb-3 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-gray-800">
                  <span class="text-xs font-semibold text-gray-600 dark:text-gray-400">COT-2026-0148</span>
                  <span class="rounded-full bg-success-50 px-2 py-0.5 text-[11px] font-semibold text-success-600 dark:bg-success-500/15 dark:text-success-400">Aprobada</span>
                </div>
                <div class="space-y-2">
                  @for (line of quoteLines; track line.label) {
                    <div class="flex items-center justify-between text-xs">
                      <span class="text-gray-500 dark:text-gray-400">{{ line.label }}</span>
                      <span class="font-medium text-gray-700 dark:text-gray-300">{{ line.value }}</span>
                    </div>
                  }
                </div>
              </div>
            </article>

            <!-- Chatflows -->
            <article class="reveal col-span-12 rounded-2xl border border-gray-200 bg-white p-7 shadow-sm transition-colors hover:border-brand-500/40 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-4">
              <div class="mb-6 flex items-start gap-3">
                <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-500 text-white shadow-theme-sm">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M21 12a8 8 0 01-11.6 7.1L3 21l1.9-6.4A8 8 0 1121 12z" />
                  </svg>
                </span>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Chatflows con IA</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Bot, chat en vivo y encuestas CSAT</p>
                </div>
              </div>
              <div class="space-y-2.5">
                <div class="max-w-[85%] rounded-2xl rounded-tl-sm bg-gray-100 px-3.5 py-2 text-xs text-gray-700 dark:bg-white/[0.06] dark:text-gray-300">
                  Hola, ¿tienen precios para 20 usuarios?
                </div>
                <div class="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-brand-500 px-3.5 py-2 text-xs text-white">
                  ¡Claro! Te preparo una cotización y te la envío por correo.
                </div>
                <div class="max-w-[85%] rounded-2xl rounded-tl-sm bg-gray-100 px-3.5 py-2 text-xs text-gray-700 dark:bg-white/[0.06] dark:text-gray-300">
                  Perfecto, gracias.
                </div>
              </div>
            </article>

            <!-- Módulos secundarios -->
            @for (extra of extraModules; track extra.title) {
              <article class="reveal col-span-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-colors hover:border-brand-500/40 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-3">
                <span class="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="extra.path" />
                  </svg>
                </span>
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{{ extra.title }}</h3>
                <p class="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{{ extra.description }}</p>
              </article>
            }
          </div>
        </div>
      </section>

      <!-- ============================= MÉTRICAS =========================== -->
      <section class="border-y border-gray-200 bg-gray-50 py-16 dark:border-gray-800 dark:bg-white/[0.02]">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="grid grid-cols-2 gap-8 md:grid-cols-4">
            @for (stat of stats; track stat.label) {
              <div class="reveal text-center">
                <p class="bg-gradient-to-r from-brand-500 to-brand-400 bg-clip-text text-4xl font-bold text-transparent sm:text-5xl">{{ stat.value }}</p>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ stat.label }}</p>
              </div>
            }
          </div>
        </div>
      </section>

      <!-- ============================ SEGURIDAD =========================== -->
      <section id="security" class="py-24">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="grid items-center gap-14 lg:grid-cols-2">
            <div class="reveal">
              <span class="inline-block rounded-full border border-brand-500/20 bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-600 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-300">
                Control
              </span>
              <h2 class="mt-5 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                Cada dato, con dueño y con historia
              </h2>
              <p class="mt-4 text-gray-600 dark:text-gray-400">
                Partner CRM es multiempresa de origen: los datos de cada organización viven
                aislados, los permisos se definen por rol y por registro, y todo cambio queda
                registrado con quién, cuándo y qué cambió.
              </p>
              <a routerLink="/signup"
                class="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-500 transition-colors hover:text-brand-600">
                Ver cómo funciona
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>

            <div class="grid gap-4 sm:grid-cols-2">
              @for (item of securityItems; track item.title) {
                <div class="reveal rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
                  <span class="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.path" />
                    </svg>
                  </span>
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{{ item.title }}</h3>
                  <p class="mt-1.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{{ item.description }}</p>
                </div>
              }
            </div>
          </div>
        </div>
      </section>

      <!-- =========================== TESTIMONIOS ========================== -->
      <section id="testimonials" class="border-y border-gray-200 bg-gray-50 py-24 dark:border-gray-800 dark:bg-white/[0.02]">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="reveal mx-auto mb-14 max-w-2xl text-center">
            <span class="inline-block rounded-full border border-brand-500/20 bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-600 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-300">
              Testimonios
            </span>
            <h2 class="mt-5 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Lo que dicen nuestros clientes
            </h2>
          </div>

          <div class="grid gap-6 md:grid-cols-3">
            @for (testimonial of testimonials; track testimonial.name) {
              <figure class="reveal flex flex-col rounded-2xl border border-gray-200 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:border-brand-500/40 dark:border-gray-800 dark:bg-gray-900">
                <div class="mb-5 flex gap-0.5 text-brand-500">
                  @for (star of [1, 2, 3, 4, 5]; track star) {
                    <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  }
                </div>
                <blockquote class="flex-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  «{{ testimonial.content }}»
                </blockquote>
                <figcaption class="mt-6 flex items-center gap-3 border-t border-gray-200 pt-5 dark:border-gray-800">
                  <span class="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">{{ testimonial.initials }}</span>
                  <span>
                    <span class="block text-sm font-semibold text-gray-900 dark:text-white">{{ testimonial.name }}</span>
                    <span class="block text-xs text-gray-500 dark:text-gray-400">{{ testimonial.role }}</span>
                  </span>
                </figcaption>
              </figure>
            }
          </div>
        </div>
      </section>

      <!-- ============================= PRECIOS ============================ -->
      <section id="pricing" class="py-24">
        <div class="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div class="reveal mx-auto mb-14 max-w-2xl text-center">
            <span class="inline-block rounded-full border border-brand-500/20 bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-600 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-300">
              Precios
            </span>
            <h2 class="mt-5 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Planes a la medida
            </h2>
            <p class="mt-4 text-gray-600 dark:text-gray-400">
              Cada operación es distinta. Cuéntanos cómo trabaja tu equipo y te preparamos una
              propuesta con el alcance y el precio que le corresponden.
            </p>
          </div>

          <div class="mx-auto grid max-w-3xl gap-6 md:grid-cols-2">
            @for (plan of pricingPlans; track plan.name) {
              <div class="reveal relative flex flex-col rounded-2xl border p-8 shadow-sm transition-all"
                [class]="plan.popular
                  ? 'border-brand-500 bg-white shadow-theme-lg dark:bg-white/[0.05]'
                  : 'border-gray-200 bg-white hover:border-brand-500/40 dark:border-gray-800 dark:bg-white/[0.03]'">
                @if (plan.popular) {
                  <span class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-500 px-3.5 py-1 text-xs font-semibold text-white shadow-theme-sm">
                    Más popular
                  </span>
                }
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ plan.name }}</h3>
                <p class="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{{ plan.description }}</p>
                <p class="mt-6 text-3xl font-bold text-gray-900 dark:text-white">{{ plan.price }}</p>
                <ul class="mt-6 flex-1 space-y-3">
                  @for (feature of plan.features; track feature) {
                    <li class="flex items-start gap-2.5 text-sm text-gray-600 dark:text-gray-400">
                      <svg class="mt-0.5 h-4 w-4 shrink-0 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                      </svg>
                      {{ feature }}
                    </li>
                  }
                </ul>
                <a routerLink="/signup"
                  class="mt-8 block rounded-xl px-6 py-3 text-center text-sm font-semibold transition-colors"
                  [class]="plan.popular
                    ? 'bg-brand-500 text-white hover:bg-brand-600'
                    : 'border border-gray-200 text-gray-700 hover:border-brand-500 hover:text-brand-500 dark:border-gray-800 dark:text-gray-300'">
                  {{ plan.cta }}
                </a>
              </div>
            }
          </div>
        </div>
      </section>

      <!-- ============================ CTA FINAL =========================== -->
      <section class="px-4 pb-24 sm:px-6 lg:px-8">
        <div class="reveal relative mx-auto max-w-5xl overflow-hidden rounded-3xl bg-brand-950 px-6 py-16 text-center sm:px-16">
          <div class="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-brand-500/30 blur-[100px]"></div>
          <div class="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-brand-400/20 blur-[100px]"></div>

          <div class="relative">
            <h2 class="text-3xl font-bold tracking-tight text-white sm:text-4xl">Empieza hoy mismo</h2>
            <p class="mx-auto mt-4 max-w-xl text-base text-brand-100">
              Cuéntanos cómo vende tu equipo y te mostramos Partner CRM funcionando con tu propio proceso.
            </p>
            <div class="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <a routerLink="/signup"
                class="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-3.5 text-base font-semibold text-brand-600 shadow-theme-md transition-all hover:-translate-y-0.5 active:translate-y-0">
                Solicitar acceso
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a routerLink="/signin"
                class="inline-flex items-center justify-center rounded-xl border border-white/25 bg-white/10 px-7 py-3.5 text-base font-medium text-white backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:bg-white/15 active:translate-y-0">
                Iniciar sesión
              </a>
            </div>
          </div>
        </div>
      </section>

      <!-- ============================== FOOTER ============================ -->
      <footer class="border-t border-gray-200 py-14 dark:border-gray-800">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="mb-12 grid gap-10 md:grid-cols-4">
            <div>
              <a routerLink="/" class="flex items-center" aria-label="Partner CRM">
                <img src="images/logo-crm/partner-crm-full.png" alt="Partner CRM"
                  class="h-9 w-auto object-contain dark:hidden" />
                <img src="images/logo-crm/partner-crm-full-white.png" alt="Partner CRM"
                  class="hidden h-9 w-auto object-contain dark:block" />
              </a>
              <p class="mt-5 max-w-xs text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                La plataforma de gestión comercial para equipos que venden y dan servicio en el mismo lugar.
              </p>
            </div>

            @for (group of footerGroups; track group.title) {
              <div>
                <h4 class="text-sm font-semibold text-gray-900 dark:text-white">{{ group.title }}</h4>
                <ul class="mt-4 space-y-3">
                  @for (link of group.links; track link.label) {
                    <li>
                      @if (link.section) {
                        <a (click)="scrollToSection(link.section); $event.preventDefault()"
                          class="cursor-pointer text-sm text-gray-500 transition-colors hover:text-brand-500 dark:text-gray-400">
                          {{ link.label }}
                        </a>
                      } @else if (link.routerLink) {
                        <a [routerLink]="link.routerLink"
                          class="text-sm text-gray-500 transition-colors hover:text-brand-500 dark:text-gray-400">
                          {{ link.label }}
                        </a>
                      } @else {
                        <a [href]="link.href"
                          class="text-sm text-gray-500 transition-colors hover:text-brand-500 dark:text-gray-400">
                          {{ link.label }}
                        </a>
                      }
                    </li>
                  }
                </ul>
              </div>
            }
          </div>

          <div class="flex flex-col items-center justify-between gap-4 border-t border-gray-200 pt-8 dark:border-gray-800 md:flex-row">
            <div class="space-y-1 text-center md:text-left">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                © {{ year }} PARTNER TECH E.I.R.L. Todos los derechos reservados.
              </p>
              <p class="text-xs text-gray-400 dark:text-gray-500">
                Av. Malachowsky N.º 340, Dpto. 401, Urb. Limatambo Sect. 14, San Borja, Lima, Lima – Perú
                &middot;
                <a href="mailto:crm@partnertech.pe" class="transition-colors hover:text-brand-500">crm&#64;partnertech.pe</a>
              </p>
            </div>
            <div class="flex items-center gap-2">
              <a href="#" aria-label="LinkedIn"
                class="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:border-brand-500 hover:text-brand-500 dark:border-gray-800 dark:text-gray-400">
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
              <a href="#" aria-label="X"
                class="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:border-brand-500 hover:text-brand-500 dark:border-gray-800 dark:text-gray-400">
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="#" aria-label="YouTube"
                class="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:border-brand-500 hover:text-brand-500 dark:border-gray-800 dark:text-gray-400">
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      <div class="fixed bottom-6 right-6 z-50">
        <app-theme-toggle-two />
      </div>
    </div>
  `
})
export class LandingComponent implements AfterViewInit, OnDestroy {
  private readonly host = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);

  scrolled = false;
  mobileOpen = false;
  readonly year = new Date().getFullYear();

  private observer?: IntersectionObserver;

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.scrolled = window.scrollY > 16;
  }

  ngAfterViewInit(): void {
    // Sin IntersectionObserver todo queda visible: .reveal solo debe ocultar
    // cuando sabemos que algo podra revelarlo despues.
    const elements = Array.from(
      (this.host.nativeElement as HTMLElement).querySelectorAll<HTMLElement>('.reveal')
    );

    if (typeof IntersectionObserver === 'undefined') {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.observer?.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );

    elements.forEach((el) => this.observer!.observe(el));
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (!element) return;

    const top = element.getBoundingClientRect().top + window.scrollY - 96;
    const before = window.scrollY;
    window.scrollTo({ top, behavior: 'smooth' });

    // Hay navegadores y ajustes de sistema (animaciones desactivadas, algunos
    // perfiles de Chrome) donde `behavior: 'smooth'` se ignora en silencio y el
    // enlace del menu no lleva a ninguna parte. Si a los 150 ms no nos movimos,
    // saltamos sin animacion: mejor un salto seco que un menu muerto.
    window.setTimeout(() => {
      if (Math.abs(window.scrollY - before) < 2 && Math.abs(top - before) >= 2) {
        window.scrollTo(0, top);
      }
    }, 150);
  }

  readonly navLinks = [
    { id: 'features', label: 'Módulos' },
    { id: 'security', label: 'Seguridad' },
    { id: 'pricing', label: 'Precios' },
    { id: 'testimonials', label: 'Testimonios' }
  ];

  readonly heroPoints = ['Multiempresa', 'Auditoría completa', 'Roles y permisos', 'Soporte en español'];

  readonly mockMenu = [
    { label: 'Dashboard', active: false },
    { label: 'Contactos', active: false },
    { label: 'Negocios', active: true },
    { label: 'Cotizaciones', active: false },
    { label: 'Tickets', active: false },
    { label: 'Workflows', active: false }
  ];

  readonly mockKpis = [
    { value: '1,247', label: 'Contactos', width: '76%' },
    { value: '89', label: 'Leads activos', width: '58%' },
    { value: '142K', label: 'Pipeline', width: '88%' }
  ];

  readonly mockKanban = [
    { title: 'Calificado', count: 6, cards: [{ name: 'TechSolutions', amount: '12,400' }, { name: 'Innovatech', amount: '8,900' }] },
    { title: 'Propuesta', count: 4, cards: [{ name: 'CloudCo', amount: '31,000' }] },
    { title: 'Cierre', count: 3, cards: [{ name: 'Grupo Andes', amount: '54,200' }] }
  ];

  readonly pipeline = [
    { title: 'Leads', count: 12, bars: [1, 2] },
    { title: 'Calificados', count: 8, bars: [1] },
    { title: 'Propuesta', count: 5, bars: [1, 2] },
    { title: 'Cierre', count: 3, bars: [1] }
  ];

  readonly workflowSteps = [
    { label: 'Entra un lead nuevo', tone: 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300' },
    { label: 'Se asigna al vendedor de la zona', tone: 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300' },
    { label: 'Correo de bienvenida y tarea de seguimiento', tone: 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300' },
    { label: 'Si no responde en 48 h, recordatorio', tone: 'bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-400' }
  ];

  readonly ticketRows = [
    { label: 'Abiertos', value: '12', tone: 'text-brand-500' },
    { label: 'En proceso', value: '8', tone: 'text-orange-500' },
    { label: 'Resueltos', value: '45', tone: 'text-success-500' }
  ];

  readonly quoteLines = [
    { label: 'Licencia anual x 20', value: '24,000' },
    { label: 'Implementación', value: '4,500' },
    { label: 'Descuento', value: '-2,850' },
    { label: 'Total', value: '25,650' }
  ];

  readonly extraModules = [
    {
      title: 'Marketing por correo',
      description: 'Campañas, segmentos y plantillas con editor visual.',
      path: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
    },
    {
      title: 'Formularios',
      description: 'Captura leads desde tu web y mapéalos al CRM.',
      path: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
    },
    {
      title: 'Reuniones',
      description: 'Página de reserva pública, recordatorios y videollamada.',
      path: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
    },
    {
      title: 'Productos y precios',
      description: 'Catálogo, paquetes y precios escalonados por volumen.',
      path: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
    }
  ];

  readonly securityItems = [
    {
      title: 'Multiempresa real',
      description: 'Cada consulta filtra por empresa. Un usuario puede pertenecer a varias y cambiar entre ellas.',
      path: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
    },
    {
      title: 'Roles y permisos',
      description: 'Roles a medida con permisos por módulo y por registro, no solo por pantalla.',
      path: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
    },
    {
      title: 'Auditoría de cambios',
      description: 'Línea de tiempo por registro: quién cambió qué y cuándo.',
      path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
    },
    {
      title: 'Notificaciones',
      description: 'Avisos in-app y por correo cuando algo te toca a ti o a tu equipo.',
      path: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
    }
  ];

  readonly stats = [
    { value: '500+', label: 'Equipos activos' },
    { value: '2.5M+', label: 'Leads gestionados' },
    { value: '45%', label: 'Más cierres' },
    { value: '99.9%', label: 'Uptime' }
  ];

  readonly testimonials = [
    { name: 'Carolina Martínez', initials: 'CM', role: 'Directora Comercial, TechCorp', content: 'Partner CRM transformó nuestra forma de hacer seguimiento a leads. Pasamos de perder el 40% de oportunidades a cerrar el 90% de nuestro pipeline.' },
    { name: 'Roberto Sánchez', initials: 'RS', role: 'CEO, SalesPro Agency', content: 'La automatización nos ahorra 15 horas semanales en tareas administrativas. Ahora mi equipo se enfoca en lo que realmente importa: vender.' },
    { name: 'Ana López', initials: 'AL', role: 'Head of Support, HelpDesk Inc', content: 'Los tickets se gestionan el doble de rápido con las reglas de automatización. La satisfacción de nuestros clientes subió un 35%.' }
  ];

  // Partner CRM es un producto de pago que se vende de forma asistida: no hay plan
  // gratuito ni alta en auto-servicio, y el precio se cotiza por cliente. De ahi
  // 'A consultar' en vez de una cifra, y CTAs que llevan a /signup (solicitud).
  readonly pricingPlans = [
    { name: 'Professional', description: 'Para equipos en crecimiento', price: 'A consultar', popular: true, features: ['Usuarios ilimitados', '10,000 contactos', 'Pipeline completo', 'Automatizaciones', 'Reportes avanzados', 'Integraciones', 'Soporte prioritario'], cta: 'Solicitar demostración' },
    { name: 'Enterprise', description: 'Para grandes organizaciones', price: 'A consultar', popular: false, features: ['Todo lo de Professional', 'Contactos ilimitados', 'SSO y seguridad', 'API completa', 'Account manager', 'SLA garantizado'], cta: 'Contactar ventas' }
  ];

  readonly footerGroups: Array<{ title: string; links: Array<{ label: string; section: string | null; routerLink: string | null; href: string | null }> }> = [
    {
      title: 'Producto',
      links: [
        { label: 'Módulos', section: 'features', routerLink: null, href: null },
        { label: 'Seguridad', section: 'security', routerLink: null, href: null },
        { label: 'Precios', section: 'pricing', routerLink: null, href: null },
        { label: 'Testimonios', section: 'testimonials', routerLink: null, href: null }
      ]
    },
    {
      title: 'Cuenta',
      links: [
        { label: 'Iniciar sesión', section: null, routerLink: '/signin', href: null },
        { label: 'Solicitar acceso', section: null, routerLink: '/signup', href: null }
      ]
    },
    {
      title: 'Legal',
      links: [
        { label: 'Política de privacidad', section: null, routerLink: null, href: '/privacy' },
        { label: 'Términos de servicio', section: null, routerLink: null, href: '/terms' },
        { label: 'Términos y condiciones', section: null, routerLink: '/terminos-y-condiciones', href: null }
      ]
    }
  ];

  /**
   * Integraciones REALES del producto (OAuth de Google/Microsoft para correo y
   * calendario, WhatsApp Business, Twilio Voice, Jitsi para videollamada y los
   * proveedores de correo saliente). No se listan marcas que el CRM no integra.
   */
  private readonly integrationMarks: Array<{ name: string; svg: string }> = [
    { name: 'Google Workspace', svg: '<svg viewBox="0 0 24 24" fill="currentColor" class="h-6 w-6"><path d="M12.24 10.29v3.62h5.05c-.22 1.32-1.6 3.87-5.05 3.87-3.04 0-5.52-2.52-5.52-5.62s2.48-5.62 5.52-5.62c1.73 0 2.89.74 3.55 1.37l2.42-2.33C16.66 4.1 14.66 3.2 12.24 3.2 7.42 3.2 3.5 7.13 3.5 12s3.92 8.8 8.74 8.8c5.05 0 8.4-3.55 8.4-8.55 0-.58-.06-1.02-.14-1.46z"/></svg>' },
    { name: 'Microsoft 365', svg: '<svg viewBox="0 0 24 24" fill="currentColor" class="h-6 w-6"><path d="M3 3h8.5v8.5H3zM12.5 3H21v8.5h-8.5zM3 12.5h8.5V21H3zM12.5 12.5H21V21h-8.5z"/></svg>' },
    { name: 'WhatsApp Business', svg: '<svg viewBox="0 0 24 24" fill="currentColor" class="h-6 w-6"><path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.65-2.05-.17-.3-.02-.46.13-.6.14-.14.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.04 21.5a9.4 9.4 0 01-4.8-1.31l-.34-.2-3.56.93.95-3.47-.22-.36a9.36 9.36 0 01-1.44-5.01c0-5.18 4.23-9.4 9.42-9.4a9.34 9.34 0 016.65 2.76 9.28 9.28 0 012.75 6.65c0 5.18-4.23 9.41-9.41 9.41zM20.5 3.49A11.78 11.78 0 0012.04 0C5.5 0 .18 5.32.17 11.86c0 2.09.55 4.13 1.59 5.93L.07 24l6.36-1.66a11.86 11.86 0 005.6 1.43c6.55 0 11.87-5.32 11.88-11.86 0-3.17-1.24-6.15-3.48-8.4z"/></svg>' },
    { name: 'Twilio Voice', svg: '<svg viewBox="0 0 24 24" fill="currentColor" class="h-6 w-6"><path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 21.6c-5.3 0-9.6-4.3-9.6-9.6S6.7 2.4 12 2.4s9.6 4.3 9.6 9.6-4.3 9.6-9.6 9.6zM9.15 7.35a1.8 1.8 0 11-3.6 0 1.8 1.8 0 013.6 0zm9.3 0a1.8 1.8 0 11-3.6 0 1.8 1.8 0 013.6 0zm0 9.3a1.8 1.8 0 11-3.6 0 1.8 1.8 0 013.6 0zm-9.3 0a1.8 1.8 0 11-3.6 0 1.8 1.8 0 013.6 0z"/></svg>' },
    { name: 'Jitsi Meet', svg: '<svg viewBox="0 0 24 24" fill="currentColor" class="h-6 w-6"><path d="M15 10.5V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2h9a2 2 0 002-2v-3.5l5 3.5V7l-5 3.5z"/></svg>' },
    { name: 'Mailgun', svg: '<svg viewBox="0 0 24 24" fill="currentColor" class="h-6 w-6"><path d="M3 5h18a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V6a1 1 0 011-1zm1.4 2L12 12.3 19.6 7H4.4zM20 8.9l-7.4 5.2a1 1 0 01-1.2 0L4 8.9V17h16V8.9z"/></svg>' }
  ];

  /**
   * La cinta se desplaza -50%, asi que la lista va DUPLICADA para que el bucle
   * sea continuo. Los SVG son constantes del propio componente (no entrada de
   * usuario), por eso el bypass del sanitizador es seguro aqui.
   */
  readonly marqueeIntegrations: Array<{ name: string; icon: SafeHtml }> = [
    ...this.integrationMarks,
    ...this.integrationMarks
  ].map((mark) => ({ name: mark.name, icon: this.sanitizer.bypassSecurityTrustHtml(mark.svg) }));
}
